import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { message, Spin, Result, Button, Card, Typography, Descriptions, Space } from 'antd';
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { customFetch } from '../utils/api';

const { Title, Paragraph } = Typography;

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';

interface FileMeta {
    id: string;
    file_name: string;
    download_url: string;
    size: number;
    product_name: string;
    product_version: string;
    uploaded_at: string;
    scope: string;
    description: string;
}

function formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

const triggerBrowserDownload = async (response: Response, fileName: string) => {
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
};

type Status = 'loading' | 'ready' | 'downloading' | 'success' | 'error';

export const DownloadHandlerPage: React.FC = () => {
    const { fileId } = useParams<{ fileId: string }>();
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>('loading');
    const [error, setError] = useState<string | null>(null);
    const [fileMeta, setFileMeta] = useState<FileMeta | null>(null);

    // Step 1: Fetch metadata and decide what to show
    useEffect(() => {
        const fetchFileInfo = async () => {
            if (!fileId) {
                setError("Invalid download link. No file ID was provided.");
                setStatus('error');
                return;
            }

            try {
                const metaResponse = await customFetch(`/api/s3/public/files/${fileId}/`);
                if (!metaResponse.ok) throw new Error("Could not find file information.");
                const data = await metaResponse.json();
                setFileMeta(data);

                if (data.scope === 'public') {
                    // Public file: show landing page with file details
                    setStatus('ready');
                } else {
                    // Non-public file: require login
                    if (!isAuthenticated) {
                        message.info("Please log in to download this file.");
                        navigate(`/login?next=/download/${fileId}`);
                    } else {
                        // Authenticated user: redirect to downloads page
                        navigate('/downloads');
                    }
                }
            } catch (err: any) {
                setError(err.message || "An unexpected error occurred.");
                setStatus('error');
            }
        };

        if (!isAuthLoading) {
            fetchFileInfo();
        }
    }, [fileId, isAuthLoading, isAuthenticated, navigate]);

    // Step 2: User clicks download button
    const handleDownload = async () => {
        if (!fileMeta) return;
        setStatus('downloading');

        try {
            const downloadResponse = await fetch(`${API_BASE_URL}${fileMeta.download_url}`);
            if (!downloadResponse.ok) throw new Error("Download failed.");
            await triggerBrowserDownload(downloadResponse, fileMeta.file_name);
            setStatus('success');
        } catch (err: any) {
            setError(err.message || "An unexpected error occurred during download.");
            setStatus('error');
        }
    };

    // --- Render states ---

    if (status === 'loading') {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Loading file information..." />
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f0f2f5' }}>
                <Result
                    status="error"
                    title="Download Failed"
                    subTitle={error}
                    extra={[
                        <Button type="primary" key="downloads" onClick={() => navigate(isAuthenticated ? '/downloads' : '/download')}>
                            Go to Downloads
                        </Button>,
                    ]}
                />
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f0f2f5' }}>
                <Result
                    status="success"
                    title="Download Started"
                    subTitle={`"${fileMeta?.file_name}" is being downloaded.`}
                    extra={[
                        <Button type="primary" key="downloads" onClick={() => navigate(isAuthenticated ? '/downloads' : '/download')}>
                            Browse More Downloads
                        </Button>,
                    ]}
                />
            </div>
        );
    }

    // status === 'ready' or 'downloading': show the landing page
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f0f2f5', padding: '24px' }}>
            <Card
                style={{ maxWidth: 560, width: '100%', borderRadius: 8 }}
            >
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <FileTextOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                    <Title level={3} style={{ margin: 0 }}>
                        {fileMeta?.product_name || 'File Download'}
                    </Title>
                    {fileMeta?.product_version && (
                        <Paragraph type="secondary" style={{ margin: '4px 0 0' }}>
                            Version {fileMeta.product_version}
                        </Paragraph>
                    )}
                </div>

                {fileMeta?.description && (
                    <Paragraph style={{ marginBottom: 24 }}>{fileMeta.description}</Paragraph>
                )}

                <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="File Name">{fileMeta?.file_name}</Descriptions.Item>
                    <Descriptions.Item label="Size">{formatBytes(fileMeta?.size || 0)}</Descriptions.Item>
                    <Descriptions.Item label="Released">{fileMeta?.uploaded_at ? new Date(fileMeta.uploaded_at).toLocaleDateString() : 'N/A'}</Descriptions.Item>
                </Descriptions>

                <div style={{ textAlign: 'center', marginTop: 32 }}>
                    <Space direction="vertical" size="middle">
                        <Button
                            type="primary"
                            size="large"
                            icon={<DownloadOutlined />}
                            loading={status === 'downloading'}
                            onClick={handleDownload}
                        >
                            {status === 'downloading' ? 'Downloading...' : 'Download'}
                        </Button>
                        <Button type="link" onClick={() => navigate(isAuthenticated ? '/downloads' : '/download')}>
                            Browse More Downloads
                        </Button>
                    </Space>
                </div>
            </Card>
        </div>
    );
};
